import { useCallback, useEffect, useState, useRef } from "react";
import {
  trackVideoPause,
  trackVideoPlay,
} from "../VideoPlayer/track-video-events";
import type { AffixProps, CarouselEvent } from "./types";
import { setCurrentIndex } from "./Carousel.store";

type CarouselHookProps = {
  type?: "carousel" | "slider";
  updateAction?: (index: number) => void;
  // Glide instance reference, and event handler. This
  // is not a standard event so we need any. Glide also doens't have
  // any types so we can't really type this properly.

  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  glideInstance: any;
  onEvent?: (event: CarouselEvent) => void;
  totalSlides: number;
  id?: string;
};

export const useCarousel = ({
  type = "carousel",
  updateAction,
  onEvent,
  glideInstance,
  totalSlides,
  id,
}: CarouselHookProps) => {
  const sliderIndex = useRef(0);
  const [lockedNext, setLockedNext] = useState(false);
  const [lockedPrev, setLockedPrev] = useState(true);

  // Helper function to update video state
  const handleVideoState = useCallback(
    (video: HTMLVideoElement | null, isPlaying: string | null) => {
      if (!video) return;
      const videoSlug = video.getAttribute("data-video-slug");

      if (isPlaying === "playing") {
        video.pause();
        trackVideoPause(video, videoSlug);
      } else if (isPlaying === "paused") {
        video.play();
        trackVideoPlay(video, videoSlug);
      }
    },
    []
  );

  // Updates the current slide index and handles video state
  const updateSlide = useCallback(
    (index: number) => {
      sliderIndex.current = index;
      setCurrentIndex(index);
      glideInstance.current.go(`=${index}`);
      updateAction?.(index);

      const video = document.querySelector(
        "[data-video-type=CarouselVideo]"
      ) as HTMLVideoElement;
      const isPlaying = video?.getAttribute("data-is-playing");

      handleVideoState(video, isPlaying);
    },
    [updateAction, glideInstance, handleVideoState]
  );

  // Handles slide navigation
  const handleSlideChange = useCallback(
    (action: "next" | "prev") => {
      const { perView } = glideInstance.current.settings;
      const isNext = action === "next";
      const nextIndex = sliderIndex.current + (isNext ? 1 : -1);
      const atBoundary = isNext ? nextIndex >= totalSlides : nextIndex < 0;

      if (
        type === "slider" &&
        ((lockedNext && isNext) || (lockedPrev && !isNext))
      )
        return;
      if (atBoundary && type === "carousel") {
        updateSlide(isNext ? 0 : totalSlides - 1);
        return;
      }

      if (type === "slider") {
        setLockedNext(nextIndex >= totalSlides - perView);
        setLockedPrev(nextIndex <= 0);
      }

      updateSlide(nextIndex);
    },
    [glideInstance, totalSlides, type, lockedNext, lockedPrev, updateSlide]
  );

  // Handles both keyboard and affix (arrow) actions
  const handleAffixAction = useCallback(
    (action: AffixProps["action"] | KeyboardEvent) => {
      const previousIndex = glideInstance.current.index;

      if (action instanceof KeyboardEvent) {
        if (action.key === "ArrowRight") handleSlideChange("next");
        else if (action.key === "ArrowLeft") handleSlideChange("prev");
      } else {
        handleSlideChange(action);
      }

      const eventDetails: CarouselEvent = {
        previousPosition: previousIndex + 1,
        newPosition: glideInstance.current.index + 1,
        previousIndex,
        newIndex: glideInstance.current.index,
        direction:
          previousIndex > glideInstance.current.index ? "backwards" : "forward",
        type: "navigation",
        id,
      };

      onEvent?.(eventDetails);
    },
    [glideInstance, handleSlideChange, onEvent, id]
  );

  // Attaches keyboard event listeners
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const focusedElement = document.activeElement;
      const carouselElement = document.getElementById(id!);

      if (
        focusedElement === carouselElement ||
        carouselElement?.contains(focusedElement)
      ) {
        handleAffixAction(event);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleAffixAction, id]);

  // Initializes Glide instance and attaches event handlers
  useEffect(() => {
    const glide = glideInstance.current;
    if (!glide) return;

    glide.enable();
    const handleRunEvents = () => setCurrentIndex(glide.index);

    glide.on(["move", "run"], handleRunEvents);
    glide.on(["swipe.end"], () => {
      const eventDetails: CarouselEvent = {
        previousPosition: sliderIndex.current + 1,
        newPosition: glide.index + 1,
        previousIndex: sliderIndex.current,
        newIndex: glide.index,
        direction: sliderIndex.current > glide.index ? "backwards" : "forward",
        type: "swipe",
        id,
      };
      sliderIndex.current = glide.index;
      onEvent?.(eventDetails);
    });

    return () => glide.on(["move", "run"], () => {});
  }, [glideInstance, onEvent, id]);

  return {
    updateSlide,
    handleAffixAction,
    glideInstance,
    lockedNext,
    lockedPrev,
  };
};
